import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import {
  purchaseOrderService,
  CancellationRequestDTO,
  CancellationRequestStatus,
} from '../../services/purchaseOrderService';
import './PurchaseOrderPage.css';

const REQUEST_STATUS_BADGE: Record<CancellationRequestStatus, string> = {
  Pending: 'ims-badge ims-badge-pending',
  Approved: 'ims-badge ims-badge-approved',
  Rejected: 'ims-badge ims-badge-rejected',
  Withdrawn: 'ims-badge ims-badge-draft',
};

const PurchaseOrderCancellationApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pendingRequests, setPendingRequests] = useState<CancellationRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    loadPending();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await purchaseOrderService.getPendingCancellationApprovals({
        pageNumber: page,
        pageSize: PAGE_SIZE,
      });
      if (res?.data) {
        const body = res.data as unknown as { data?: CancellationRequestDTO[]; pagination?: { total: number } };
        if (Array.isArray(body?.data)) {
          setPendingRequests(body.data);
          setTotalCount(body.pagination?.total ?? body.data.length);
        } else if (Array.isArray(res.data)) {
          setPendingRequests(res.data as unknown as CancellationRequestDTO[]);
          setTotalCount((res.data as unknown as CancellationRequestDTO[]).length);
        }
      }
    } catch {
      setError(t('purchaseOrder.cancellation.failedToLoadPending'));
    } finally {
      setLoading(false);
    }
  };

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmt = (v: number) =>
    new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <Layout>
      <div className="po-approvals-page">

        {/* Header */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">
              {t('purchaseOrder.cancellation.pendingApprovalsTitle')}
            </h1>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              {t('purchaseOrder.cancellation.pendingApprovalsSubtitle')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/purchase-orders/approvals')}
            >
              {t('purchaseOrder.approval.pendingApprovalsTitle')}
            </button>
            <button className="btn-refresh" onClick={loadPending}>
              <RefreshCw size={14} strokeWidth={2} />
              {t('common.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {loading ? (
          <div className="ims-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : pendingRequests.length === 0 ? (
          <div className="po-empty-state">
            <div className="po-empty-state-icon">✓</div>
            <p className="po-empty-state-title">
              {t('purchaseOrder.cancellation.noPendingApprovals')}
            </p>
            <p className="po-empty-state-sub">
              {t('purchaseOrder.cancellation.noPendingApprovalsSubtext')}
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {pendingRequests.map((req) => {
                const myApproval = req.approvals.find((a) => a.status === 'Pending');
                return (
                  <div key={req.id} className="po-approval-card po-approval-card--cancellation">
                    <div className="po-approval-card-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                            {req.poNumber}
                          </span>
                          <span className={REQUEST_STATUS_BADGE[req.status]} style={{ fontSize: 11 }}>
                            {t(`purchaseOrder.cancellation.requestStatus${req.status}`)}
                          </span>
                          {req.totalApprovalLevels > 0 && (
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {t('purchaseOrder.approval.level')} {req.currentApprovalLevel} / {req.totalApprovalLevels}
                            </span>
                          )}
                        </div>

                        <div style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>
                          <strong>{t('purchaseOrder.totalAmount')}:</strong> {fmt(req.totalAmount)}
                          {' · '}
                          <strong>{t('purchaseOrder.cancellation.requestedBy')}:</strong>{' '}
                          {req.requestedByUserName ?? `User #${req.requestedByUserId}`}
                        </div>

                        {/* Cancellation reason (truncated) */}
                        <div
                          style={{
                            fontSize: 12, color: '#6b7280',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 500,
                          }}
                          title={req.cancellationReason}
                        >
                          <strong>{t('purchaseOrder.cancellation.reason')}:</strong>{' '}
                          {req.cancellationReason}
                        </div>

                        {/* My pending approval */}
                        {myApproval && (
                          <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', fontWeight: 500 }}>
                            ⚠ {t('purchaseOrder.cancellation.assignedToYou')} — {t('purchaseOrder.approval.level')} {myApproval.approvalLevel}
                          </div>
                        )}
                      </div>

                      <button
                        className="ims-btn ims-btn-primary"
                        style={{ marginInlineStart: 16, flexShrink: 0 }}
                        onClick={() => navigate(`/purchase-orders/${req.purchaseOrderId}`)}
                      >
                        {t('purchaseOrder.cancellation.reviewAndAct')}
                      </button>
                    </div>

                    {/* Mini approval step chips */}
                    {req.approvals.length > 0 && (
                      <div className="po-approval-card-chips">
                        {req.approvals.map((a) => (
                          <div
                            key={a.id}
                            title={`Level ${a.approvalLevel}: ${a.assignedToUserName ?? ''} — ${a.status}`}
                            className={`po-approval-chip po-approval-chip--${a.status.toLowerCase()}`}
                          >
                            <span>
                              {a.status === 'Approved' ? '✓' : a.status === 'Rejected' ? '✕' : '●'}
                            </span>
                            <span>
                              {t('purchaseOrder.approval.level')} {a.approvalLevel}
                              {a.assignedToUserName ? ` · ${a.assignedToUserName}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="po-pagination">
                <button
                  className="ims-btn ims-btn-secondary"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ‹ {t('common.previous')}
                </button>
                <span className="po-pagination-info">
                  {t('common.page')} {page} / {totalPages}
                </span>
                <button
                  className="ims-btn ims-btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('common.next')} ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default PurchaseOrderCancellationApprovalsPage;
